// Contas do seletor de loja: distância, ordem e busca. Tudo roda no aparelho; a posição da pessoa não sai dele.

export interface Loja {
  id: string
  /** O código como o modelo o deu (texto ou número): é o que o filtro da tela do relatório espera. */
  valor: string | number | null
  nome: string
  endereco?: string | null
  cidade?: string | null
  uf?: string | null
  bandeira?: string | null
  /** Ausentes quando a loja não tem coordenada utilizável: continua na busca, fora do mapa e das distâncias. */
  lat?: number | null
  lng?: number | null
}

export interface Posicao {
  lat: number
  lng: number
  /** Raio de erro do GPS, em metros. */
  precisao: number
}

export interface LojaComDistancia extends Loja {
  /** Nulo para a loja sem coordenada. */
  km: number | null
}

const RAIO_DA_TERRA_KM = 6371

const rad = (graus: number) => (graus * Math.PI) / 180

/** Distância em linha reta entre dois pontos (fórmula de haversine), em km. */
export function distanciaKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const dLat = rad(b.lat - a.lat)
  const dLng = rad(b.lng - a.lng)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * RAIO_DA_TERRA_KM * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** "430 m", "1,4 km", "12 km". Abaixo de 50 m o GPS não separa uma loja da vizinha, então não finge exatidão. */
export function formatarDistancia(km: number): string {
  if (km < 0.05) return 'menos de 50 m'
  if (km < 1) return `${Math.round((km * 1000) / 10) * 10} m`
  if (km < 10) return `${km.toFixed(1).replace('.', ',')} km`
  return `${Math.round(km)} km`
}

export function formatarRaio(metros: number): string {
  return metros < 1000 ? `${metros} m` : `${metros / 1000} km`.replace('.', ',')
}

const temCoordenada = (l: Loja): l is Loja & { lat: number; lng: number } =>
  typeof l.lat === 'number' && typeof l.lng === 'number'

/** Todas as lojas com a distância da pessoa: as que têm coordenada primeiro, da mais perto à mais longe; as outras por nome. */
export function comDistancia(lojas: Loja[], pos: Posicao | null): LojaComDistancia[] {
  const lista = lojas.map<LojaComDistancia>(l => ({ ...l, km: pos && temCoordenada(l) ? distanciaKm(pos, l) : null }))
  return lista.sort((a, b) => {
    if (a.km != null && b.km != null) return a.km - b.km
    if (a.km != null) return -1
    if (b.km != null) return 1
    return a.nome.localeCompare(b.nome, 'pt-BR')
  })
}

export function noRaio(lojas: LojaComDistancia[], raioM: number): LojaComDistancia[] {
  return lojas.filter(l => l.km != null && l.km * 1000 <= raioM)
}

/** Minúsculas e sem acento, para a busca achar "Atacadao" digitando "atacadão". */
export function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
}

/** Procura em nome, código, endereço, cidade, UF e bandeira. Todas as palavras digitadas precisam aparecer. */
export function buscar(lojas: LojaComDistancia[], termo: string): LojaComDistancia[] {
  const palavras = normalizar(termo).split(/\s+/).filter(Boolean)
  if (palavras.length === 0) return lojas
  return lojas.filter(l => {
    const alvo = normalizar([l.nome, l.id, l.endereco, l.cidade, l.uf, l.bandeira].filter(Boolean).join(' '))
    return palavras.every(p => alvo.includes(p))
  })
}

/** Os raios oferecidos, com o inicial do relatório no lugar certo se ele não for um dos padrão. */
export function raiosOferecidos(inicial: number): number[] {
  const base = [200, 500, 1000, 2000, 5000, 10000]
  return base.includes(inicial) ? base : [...base, inicial].sort((a, b) => a - b)
}

/** Acima disto o GPS não separa lojas vizinhas, e o seletor avisa. */
export const PRECISAO_FRACA_M = 200
